import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import DashboardLayout from './Layout/DashboardLayout';
import './TokenShop.css';

const TokenShop = () => {
  const [items, setItems] = useState([]);
  const [balance, setBalance] = useState({ balance: 0, totalEarned: 0, totalSpent: 0 });
  const [purchases, setPurchases] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [activeTab, setActiveTab] = useState('shop');
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [itemsRes, balanceRes, purchasesRes, transactionsRes] = await Promise.all([
        api.get('/TokenShop/items'),
        api.get('/TokenShop/balance'),
        api.get('/TokenShop/my-purchases'),
        api.get('/TokenShop/my-transactions'),
      ]);
      setItems(itemsRes.data);
      setBalance(balanceRes.data);
      setPurchases(purchasesRes.data);
      setTransactions(transactionsRes.data);
    } catch (error) {
      console.error('Erro ao carregar loja:', error);
      toast.error('Erro ao carregar a loja de tokens');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (item) => {
    if (balance.balance < item.price) {
      toast.error(`Saldo insuficiente! Precisa de ${item.price} tokens, tem ${balance.balance}.`);
      return;
    }
    if (!window.confirm(`Comprar "${item.name}" por ${item.price} tokens?`)) return;
    
    setPurchasing(item.id);
    try {
      const res = await api.post(`/TokenShop/purchase/${item.id}`);
      toast.success(res.data.message);
      loadData();
    } catch (error) {
      toast.error(error.response?.data || 'Erro ao comprar item');
    } finally {
      setPurchasing(null);
    }
  };

  // Group items by category
  const grouped = items.reduce((acc, item) => {
    const cat = item.category || 'Geral';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(item);
    return acc;
  }, {});

  if (loading) {
    return (
      <DashboardLayout userType="client">
        <div className="ts-loading"><div className="spinner"></div><p>A carregar loja...</p></div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userType="client">
      <div className="token-shop">
        {/* Balance Header */}
        <div className="ts-balance-bar">
          <div className="ts-balance-info">
            <h1>🛒 Loja de Tokens</h1>
            <p>Troque os seus tokens por recompensas e benefícios exclusivos!</p>
          </div>
          <div className="ts-wallet">
            <div className="ts-wallet-balance">
              <span className="ts-wallet-icon">🪙</span>
              <span className="ts-wallet-amount">{balance.balance}</span>
              <span className="ts-wallet-label">tokens</span>
            </div>
            <div className="ts-wallet-stats">
              <span>📈 Ganhos: {balance.totalEarned}</span>
              <span>📉 Gastos: {balance.totalSpent}</span>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="ts-tabs">
          <button className={activeTab === 'shop' ? 'active' : ''} onClick={() => setActiveTab('shop')}>
            🛍️ Loja
          </button>
          <button className={activeTab === 'purchases' ? 'active' : ''} onClick={() => setActiveTab('purchases')}>
            📦 Minhas Compras
          </button>
          <button className={activeTab === 'history' ? 'active' : ''} onClick={() => setActiveTab('history')}>
            📜 Histórico
          </button>
        </div>

        {/* Shop Tab */}
        {activeTab === 'shop' && (
          <div className="ts-shop-content">
            {Object.keys(grouped).length === 0 && (
              <div className="ts-empty">
                <p>🏪 A loja está vazia de momento. Volte mais tarde!</p>
              </div>
            )}
            {Object.entries(grouped).map(([category, catItems]) => (
              <div key={category} className="ts-category">
                <h3 className="ts-category-title">{category}</h3>
                <div className="ts-items-grid">
                  {catItems.map(item => (
                    <div key={item.id} className="ts-item-card">
                      <div className="ts-item-icon">{item.icon}</div>
                      <div className="ts-item-info">
                        <h4>{item.name}</h4>
                        <p>{item.description}</p>
                        {item.stock >= 0 && (
                          <span className="ts-stock">{item.stock > 0 ? `${item.stock} disponíveis` : 'Esgotado'}</span>
                        )}
                      </div>
                      <div className="ts-item-price">
                        <span className="ts-price">🪙 {item.price}</span>
                        <button
                          className={`ts-buy-btn ${balance.balance < item.price || item.stock === 0 ? 'disabled' : ''}`}
                          onClick={() => handlePurchase(item)}
                          disabled={balance.balance < item.price || item.stock === 0 || purchasing === item.id}
                        >
                          {purchasing === item.id ? '...' : item.stock === 0 ? 'Esgotado' : 'Comprar'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Purchases Tab */}
        {activeTab === 'purchases' && (
          <div className="ts-purchases">
            {purchases.length === 0 ? (
              <div className="ts-empty"><p>📦 Ainda não fez nenhuma compra.</p></div>
            ) : (
              <div className="ts-purchase-list">
                {purchases.map(p => (
                  <div key={p.id} className="ts-purchase-item">
                    <span className="ts-purchase-icon">{p.itemIcon}</span>
                    <div className="ts-purchase-details">
                      <strong>{p.itemName}</strong>
                      <small>{new Date(p.purchasedAt).toLocaleString('pt-PT')}</small>
                    </div>
                    <div className="ts-purchase-cost">
                      <span>-{p.tokensSpent} 🪙</span>
                      <span className={`ts-status ${p.status}`}>{p.status === 'completed' ? '✅' : '⏳'}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* History Tab */}
        {activeTab === 'history' && (
          <div className="ts-history">
            {transactions.length === 0 ? (
              <div className="ts-empty"><p>📜 Sem transações registadas.</p></div>
            ) : (
              <div className="ts-transaction-list">
                {transactions.map(t => (
                  <div key={t.id} className={`ts-transaction ${t.amount > 0 ? 'earned' : 'spent'}`}>
                    <div className="ts-tx-icon">{t.amount > 0 ? '📈' : '📉'}</div>
                    <div className="ts-tx-info">
                      <strong>{t.description}</strong>
                      <small>{new Date(t.createdAt).toLocaleString('pt-PT')}</small>
                    </div>
                    <div className={`ts-tx-amount ${t.amount > 0 ? 'positive' : 'negative'}`}>
                      {t.amount > 0 ? '+' : ''}{t.amount} 🪙
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default TokenShop;
